import React, { useEffect, useState } from "react";
import { adminApi } from "./adminApi";
import { X } from "lucide-react";

/**
 * ImageInput — small helper that lets the user either paste a URL or upload a
 * file. Chosen file is streamed through /api/admin/upload and the returned URL
 * is stored on the field.
 */
const ImageInput = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const res = await adminApi.upload(file);
      onChange(res.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Paste URL or upload"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-canvas border border-ink/15 focus:border-ink/40 outline-none px-3 py-2 text-sm rounded"
      />
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-[0.3em] text-ink/70 hover:text-ink cursor-pointer border border-ink/25 rounded-full px-3 py-1.5 inline-flex items-center gap-1">
          {uploading ? "Uploading…" : "Upload file"}
          <input type="file" onChange={onFile} className="hidden" accept="image/*,video/mp4,video/webm" />
        </label>
        {value && (
          <a
            href={value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] uppercase tracking-[0.3em] text-ink/60 hover:text-ink underline"
          >
            preview
          </a>
        )}
      </div>
      {error && <p className="text-[10px] text-red-400 uppercase tracking-[0.2em]">{error}</p>}
    </div>
  );
};

const ArrayInput = ({ value, onChange, type }) => {
  const arr = Array.isArray(value) ? value : [];
  const setAt = (i, v) => {
    const next = [...arr];
    next[i] = v;
    onChange(next);
  };
  const removeAt = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, ""]);

  return (
    <div className="space-y-2">
      {arr.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">
            {type === "array-image" ? (
              <ImageInput value={item} onChange={(v) => setAt(i, v)} />
            ) : (
              <input
                type="text"
                value={item}
                onChange={(e) => setAt(i, e.target.value)}
                className="w-full bg-canvas border border-ink/15 focus:border-ink/40 outline-none px-3 py-2 text-sm rounded"
              />
            )}
          </div>
          <button
            type="button"
            onClick={() => removeAt(i)}
            className="w-7 h-7 rounded-full border border-ink/25 hover:border-red-500 hover:text-red-400 flex items-center justify-center"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-[10px] uppercase tracking-[0.3em] text-ink/70 hover:text-ink border border-ink/25 rounded-full px-3 py-1.5"
      >
        + Add
      </button>
    </div>
  );
};

const EditModal = ({ collection, schema, item, onClose, onSaved }) => {
  const [form, setForm] = useState(item || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isNew = !item?.id;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await adminApi.create(collection, form);
      } else {
        await adminApi.update(collection, item.id, form);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface border border-ink/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-edit-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="text-[11px] uppercase tracking-[0.3em]">
            {isNew ? `New ${schema.label}` : `Edit ${schema.label}`}
          </h3>
          <button onClick={onClose} className="text-ink/70 hover:text-ink">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {schema.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-2">
                {f.label}
                {f.required && <span className="text-red-400">*</span>}
              </label>
              {f.type === "text" && (
                <input
                  type="text"
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="w-full bg-canvas border border-ink/15 focus:border-ink/40 outline-none px-3 py-2 text-sm rounded"
                />
              )}
              {f.type === "number" && (
                <input
                  type="number"
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value === "" ? undefined : Number(e.target.value))}
                  className="w-full bg-canvas border border-ink/15 focus:border-ink/40 outline-none px-3 py-2 text-sm rounded"
                />
              )}
              {f.type === "textarea" && (
                <textarea
                  rows={4}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="w-full bg-canvas border border-ink/15 focus:border-ink/40 outline-none px-3 py-2 text-sm rounded resize-none"
                />
              )}
              {f.type === "image" && (
                <ImageInput value={form[f.key]} onChange={(v) => setField(f.key, v)} />
              )}
              {(f.type === "array-string" || f.type === "array-image") && (
                <ArrayInput value={form[f.key]} onChange={(v) => setField(f.key, v)} type={f.type} />
              )}
              {f.helper && (
                <p className="mt-1.5 text-[10px] text-ink/45">{f.helper}</p>
              )}
            </div>
          ))}
          {error && <p className="text-xs text-red-400" data-testid="admin-edit-error">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="text-[11px] uppercase tracking-[0.3em] text-ink/70 hover:text-ink px-4 py-2"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="text-[11px] uppercase tracking-[0.3em] bg-ink text-canvas px-5 py-2 rounded-full hover:opacity-90 disabled:opacity-50"
            data-testid="admin-edit-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CollectionEditor = ({ schema }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or "new"

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminApi.list(schema.key);
      setItems(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema.key]);

  const del = async (id) => {
    if (!window.confirm(`Delete ${id}?`)) return;
    await adminApi.remove(schema.key, id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55">Collection</p>
          <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight">
            {schema.label}
          </h2>
        </div>
        <button
          onClick={() => setEditing({ __new: true })}
          className="text-[11px] uppercase tracking-[0.3em] bg-ink text-canvas px-4 py-2 rounded-full hover:opacity-90"
          data-testid="admin-new-btn"
        >
          + New
        </button>
      </div>

      <div className="bg-surface border border-ink/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid={`admin-table-${schema.key}`}>
          <thead className="bg-canvas/50 text-[10px] uppercase tracking-[0.28em] text-ink/55">
            <tr>
              {schema.columns.map((c) => (
                <th key={c} className="text-left px-5 py-3">{c}</th>
              ))}
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={schema.columns.length + 1} className="px-5 py-6 text-ink/50 text-xs">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={schema.columns.length + 1} className="px-5 py-6 text-ink/50 text-xs">
                  No entries yet.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="border-t border-ink/5 hover:bg-ink/[0.03]">
                {schema.columns.map((c) => (
                  <td key={c} className="px-5 py-3 text-ink/85 align-top">
                    {String(item[c] ?? "—")}
                  </td>
                ))}
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing(item)}
                    className="text-[10px] uppercase tracking-[0.28em] text-ink/70 hover:text-ink mr-4"
                    data-testid={`admin-edit-${item.id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => del(item.id)}
                    className="text-[10px] uppercase tracking-[0.28em] text-red-400/80 hover:text-red-300"
                    data-testid={`admin-delete-${item.id}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditModal
          collection={schema.key}
          schema={schema}
          item={editing.__new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default CollectionEditor;
